import { type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { shopPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityShopPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getNonNullValues } from '@lib/helpers'
import {
  type BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext } from '@lib/routes'
import {
  defaultModuleQueryParameters,
  getShopIndexPage,
} from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type ShopPageProps = BasePageProps & {
  page: SanityShopPage
  site: SanitySiteFragment
}

const ShopPage = ({ draftMode, locale, page, site }: ShopPageProps) => {
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [...videoObjectSchemas]

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={shopPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            productType: 'd2c',
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext
): Promise<GetStaticPropsResult<ShopPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const shopIndexPage = await getShopIndexPage(sanityClient, context.locale)

  if (!shopIndexPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(shopIndexPage.page),
      site: getNonNullValues(shopIndexPage.site),
    },
    revalidate: 60,
  }
}

export default ShopPage
